import { ArrowRight, ArrowDown } from "lucide-react";
import { motion } from "motion/react";
import { useSectionParallax } from "../../hooks/useSectionParallax";

const STAGES = [
  {
    when: "Before the trial",
    product: "Foresight",
    accentCls: "text-[#5B21B6] dark:text-[#B9A5FF]",
    borderCls: "border-t-[#5B21B6] dark:border-t-[#7C4DFF]",
    body: "Commit to a dose, population, and protocol with evidence instead of assumptions, by running the trial in silico first.",
    questions: ["Does the starting dose and escalation rule hold up?", "How often does the design reach the right decision?", "How sensitive is it to our assumptions?"],
  },
  {
    when: "Once you have data",
    product: "iViz",
    accentCls: "text-[#1E4FD8] dark:text-[#4A90D9]",
    borderCls: "border-t-[#1E4FD8] dark:border-t-[#4A90D9]",
    body: "Clean, explore, and analyze any dataset with rigorous statistics, whether you write code or not.",
    questions: ["What is driving the result?", "How do the groups compare?", "Which differences are statistically meaningful?"],
  },
];

function StageCard({ stage }: { stage: (typeof STAGES)[number] }) {
  return (
    <div className={`flex-1 rounded-3xl border border-[#0A0A0F]/10 dark:border-white/10 border-t-2 ${stage.borderCls} bg-white dark:bg-[#0D0D10] p-8 md:p-10`}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C5C66] dark:text-[#9A9A9A]">{stage.when}</p>
      <p className={`mt-2 text-2xl font-normal tracking-[-0.02em] ${stage.accentCls}`}>{stage.product}</p>
      <p className="mt-4 text-base leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A]">{stage.body}</p>
      <ul className="mt-6 space-y-2.5">
        {stage.questions.map((q) => (
          <li key={q} className="text-sm font-medium text-[#0A0A0F] dark:text-white">{q}</li>
        ))}
      </ul>
    </div>
  );
}

export function HomeLifecycle() {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <section ref={ref} className="py-20 bg-[#FAFAFA] dark:bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div style={{ y: fgY }} className="text-center mb-12 max-w-2xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1E4FD8] dark:text-[#4A90D9] mb-3">How they fit together</p>
          <h2 className="text-3xl md:text-4xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white">Simulate before you commit. Analyze once you know.</h2>
          <p className="mt-4 text-base leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A]">
            Foresight helps you decide before a clinical trial. iViz helps you understand any data once you have it.
          </p>
        </motion.div>
        <motion.div style={{ y: bgY }} className="flex flex-col lg:flex-row items-stretch gap-4">
          <StageCard stage={STAGES[0]} />
          <div className="flex items-center justify-center text-[#0A0A0F]/25 dark:text-white/25" aria-hidden>
            <ArrowRight size={24} className="hidden lg:block" />
            <ArrowDown size={24} className="lg:hidden" />
          </div>
          <StageCard stage={STAGES[1]} />
        </motion.div>
      </div>
    </section>
  );
}
